// frontend/src/pages/Home/Home.js

// src/pages/Home/Home.js
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import '../../styles/Pages.css';
import './Home.css';
import { courseData } from '../../data/CoursesData';
import config from '../../config';

// ✅ Prevent browser scroll restoration BEFORE the component ever mounts,
// so there's no chance of it jumping to a previous scroll position (footer flash).
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
}

// ✅ Create axios instance with CORS configuration
const api = axios.create({
    baseURL: config.API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

// Add token to requests if available
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('admin_token');
            window.location.href = '/frontend/admin/login';
        }
        return Promise.reject(error);
    }
);

// Hero Slider Images (Static fallback)
const sliderImages = [
    { id: 1, src: '/images/slider/hero-1.jpeg', title: 'Technical Training Excellence', subtitle: "Building Pakistan's skilled workforce" },
    { id: 2, src: '/images/slider/hero-2.jpeg', title: 'Industry-Aligned Programs', subtitle: 'Precision-crafted for career success' },
    { id: 3, src: '/images/slider/hero-3.jpeg', title: 'International Standards', subtitle: 'Globally recognized certifications' },
];

// Officials Data
const officials = [
    {
        id: 1,
        name: 'Mr. Haroon Akhtar Khan',
        position: 'Special Assistant to Prime Minister',
        image: '/images/Officials/chairman.png',
        bio: 'Ministry Of Industries & Production'
    },
    {
        id: 2,
        name: 'Mr. Saif Anjum',
        position: 'Federal Secretary for Industries & Production',
        image: '/images/Officials/secretary.jpg',
        bio: 'Ministry Of Industries & Production'
    },
    {
        id: 3,
        name: 'Ms. Fareeha Ali Randhawa',
        position: 'Director General, PITAC',
        image: '/images/Officials/dg.jpg',
        bio: 'Ministry Of Industries & Production'
    },
];

// Regional Centres Data
const regionalCentres = [
    { id: 1, name: 'Quetta', region: 'Balochistan', icon: '🏔️' },
    { id: 2, name: 'Karachi', region: 'Sindh', icon: '🏙️' },
    { id: 3, name: 'Mirpur', region: 'AJK', icon: '🌄' },
    { id: 4, name: 'Peshawar', region: 'KPK', icon: '⛰️' },
    { id: 5, name: 'AJK', region: 'Azad Kashmir', icon: '🍃' },
];

// TVET Features Data
const tvetFeatures = [
    { icon: '🎓', title: 'Training Excellence', description: "Pakistan's premier technical & managerial trainings" },
    { icon: '🔧', title: 'Hands-on Skills', description: 'Practical, industry-driven curriculum' },
    { icon: '🌍', title: 'Global Standards', description: 'International certification pathways' },
    { icon: '👥', title: 'Workforce Development', description: 'Empowering youth for economic growth' },
];

// Helper function to get color for category
const getCategoryColor = (category) => {
    const colors = {
        'Designing (Mechanical)': '#2563eb',
        'Mechanical Manufacturing': '#7c3aed',
        'Civil': '#059669',
        'Welding & Fabrication': '#d97706',
        'Electrical & Electronics': '#0891b2',
        'Language': '#dc2626',
        'Health & Safety': '#8b5cf6',
        'EV': '#ec4899',
        'AI & Technology': '#f59e0b',
        'Foundry & Metal Treatment': '#14b8a6',
        'Pattern, Carpenter & CNC Wood Router': '#8b4513',
        'Renewable Energy (Solar)': '#22c55e'
    };
    return colors[category] || '#4b5563';
};

// Helper function to format date - remove time
const formatDate = (dateString) => {
    if (!dateString) return '2026';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return dateString.split('T')[0] || '2026';
        }
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (e) {
        if (dateString.includes('T')) {
            return dateString.split('T')[0];
        }
        return dateString || '2026';
    }
};

// ✅ Fallback data used as INITIAL state
const fallbackTestimonials = [
    {
        id: 1,
        name: 'Muhammad Ahmed',
        role: 'Graduate - Mechanical Engineering',
        content: 'PITAC transformed my career. The hands-on training and industry exposure helped me secure a position at a leading manufacturing company.',
        rating: 5
    },
    {
        id: 2,
        name: 'Sara Khan',
        role: 'Student - AI & Technology',
        content: 'The AI program at PITAC is world-class. The curriculum is up-to-date with industry standards.',
        rating: 5
    },
    {
        id: 3,
        name: 'Naveed Ur Rehman',
        role: 'Digital Marketing',
        content: 'PITAC changed my business! The practical digital marketing training helped me promote my business like never before.',
        rating: 5
    }
];

const fallbackPartners = [
    { id: 1, name: 'PEL', logo: '/images/partners/pel.png', icon: '🏢' },
    { id: 2, name: 'SPEL', logo: '/images/partners/spel.png', icon: '🏢' },
    { id: 3, name: 'HONDA', logo: '/images/partners/Honda.png', icon: '🏢' },
    { id: 4, name: 'HAIER', logo: '/images/partners/Haier.png', icon: '🏢' },
    { id: 5, name: 'PAKISTAN TOBACCO COMPANY', logo: '/images/partners/ptc.png', icon: '🏢' },
    { id: 6, name: 'ORIENT', logo: '/images/partners/Orient.png', icon: '🏢' },
    { id: 7, name: 'PACKAGES GROUP', logo: '/images/partners/packages.png', icon: '🏢' },
    { id: 8, name: 'UNHCR', logo: '/images/partners/unhcr.png', icon: '🏢' },
    { id: 9, name: 'NAVTTC', logo: '/images/partners/Navttc.png', icon: '🏢' },
];

const Home = () => {
    const [courses, setCourses] = useState(courseData);
    const [testimonials, setTestimonials] = useState(fallbackTestimonials);
    const [pressReleases, setPressReleases] = useState([]);
    const [partners, setPartners] = useState(fallbackPartners);
    const [loading, setLoading] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [officialsSlide, setOfficialsSlide] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState('All Categories');
    const [dynamicSliders, setDynamicSliders] = useState(sliderImages);

    // Course slider refs
    const courseSliderRef = useRef(null);
    const courseTrackRef = useRef(null);
    const courseAnimationRef = useRef(null);
    const coursePositionRef = useRef(0);
    const isCoursePausedRef = useRef(false);
    const coursePauseTimeoutRef = useRef(null);

    // Partners auto-scroll refs
    const partnersViewportRef = useRef(null);
    const partnersTrackRef = useRef(null);
    const positionRef = useRef(0);
    const isPausedRef = useRef(false);
    const pauseTimeoutRef = useRef(null);
    const rafIdRef = useRef(null);
    const [isPartnerAutoScroll, setIsPartnerAutoScroll] = useState(true);

    // ===== SCROLL TO TOP =====
    useLayoutEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        return () => {
            if ('scrollRestoration' in history) {
                history.scrollRestoration = 'auto';
            }
        };
    }, []);

    // Get unique categories from course data
    const getCourseCategories = () => {
        const data = courses.length > 0 ? courses : courseData;
        return ['All Categories', ...new Set(data.map(course => course.category))];
    };

    const categories = getCourseCategories();

    // Filter courses based on selected category
    const getFilteredCourses = () => {
        const data = courses.length > 0 ? courses : courseData;
        if (selectedCategory === 'All Categories') {
            return data;
        }
        return data.filter(course => course.category === selectedCategory);
    };

    // Get visible courses
    const getVisibleCourses = () => {
        const filtered = getFilteredCourses();
        return filtered;
    };

    // Course auto-scroll effect
    useEffect(() => {
        if (loading) return;

        const track = courseTrackRef.current;
        if (!track || getVisibleCourses().length === 0) return;

        const speed = 0.8;

        const animate = () => {
            if (!isCoursePausedRef.current && track) {
                const cardWidth = 220;
                const totalWidth = getVisibleCourses().length * cardWidth;
                const visibleWidth = courseSliderRef.current?.clientWidth || 0;

                if (totalWidth > visibleWidth) {
                    coursePositionRef.current += speed;

                    if (coursePositionRef.current >= totalWidth - visibleWidth) {
                        coursePositionRef.current = 0;
                    }

                    track.style.transform = `translateX(-${coursePositionRef.current}px)`;
                }
            }
            courseAnimationRef.current = requestAnimationFrame(animate);
        };

        courseAnimationRef.current = requestAnimationFrame(animate);
        return () => {
            if (courseAnimationRef.current) cancelAnimationFrame(courseAnimationRef.current);
        };
    }, [loading, selectedCategory, courses]);

    const handleCourseMouseEnter = () => {
        isCoursePausedRef.current = true;
        if (coursePauseTimeoutRef.current) clearTimeout(coursePauseTimeoutRef.current);
    };

    const handleCourseMouseLeave = () => {
        if (coursePauseTimeoutRef.current) clearTimeout(coursePauseTimeoutRef.current);
        coursePauseTimeoutRef.current = setTimeout(() => {
            isCoursePausedRef.current = false;
        }, 500);
    };

    // Partners auto-scroll effect
    useEffect(() => {
        if (loading || partners.length === 0 || !isPartnerAutoScroll) return;

        const track = partnersTrackRef.current;
        if (!track) return;

        const speed = 0.6;

        const animate = () => {
            if (!isPausedRef.current && track) {
                positionRef.current += speed;

                const halfWidth = track.scrollWidth / 2;
                if (halfWidth > 0 && positionRef.current >= halfWidth) {
                    positionRef.current -= halfWidth;
                }

                track.style.transform = `translateX(-${positionRef.current}px)`;
            }
            rafIdRef.current = requestAnimationFrame(animate);
        };

        rafIdRef.current = requestAnimationFrame(animate);
        return () => {
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        };
    }, [loading, partners, isPartnerAutoScroll]);

    const pauseThenResume = (delay = 3000) => {
        isPausedRef.current = true;
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = setTimeout(() => {
            isPausedRef.current = false;
            setIsPartnerAutoScroll(true);
        }, delay);
    };

    const scrollPartnersNext = () => {
        const track = partnersTrackRef.current;
        if (!track) return;

        setIsPartnerAutoScroll(false);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        isPausedRef.current = true;

        const jump = 300;
        const halfWidth = track.scrollWidth / 2;
        if (halfWidth <= 0) return;

        positionRef.current += jump;
        if (positionRef.current >= halfWidth) {
            positionRef.current -= halfWidth;
        }

        track.style.transition = 'transform 0.5s ease';
        track.style.transform = `translateX(-${positionRef.current}px)`;

        setTimeout(() => {
            if (track) track.style.transition = '';
        }, 500);

        setTimeout(() => {
            isPausedRef.current = false;
            setIsPartnerAutoScroll(true);
        }, 3000);
    };

    const scrollPartnersPrev = () => {
        const track = partnersTrackRef.current;
        if (!track) return;

        setIsPartnerAutoScroll(false);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        isPausedRef.current = true;

        const jump = 300;
        const halfWidth = track.scrollWidth / 2;
        if (halfWidth <= 0) return;

        positionRef.current -= jump;
        if (positionRef.current < 0) {
            positionRef.current += halfWidth;
        }

        track.style.transition = 'transform 0.5s ease';
        track.style.transform = `translateX(-${positionRef.current}px)`;

        setTimeout(() => {
            if (track) track.style.transition = '';
        }, 500);

        setTimeout(() => {
            isPausedRef.current = false;
            setIsPartnerAutoScroll(true);
        }, 3000);
    };

    const handlePartnersMouseEnter = () => {
        isPausedRef.current = true;
        setIsPartnerAutoScroll(false);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };

    const handlePartnersMouseLeave = () => {
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = setTimeout(() => {
            isPausedRef.current = false;
            setIsPartnerAutoScroll(true);
        }, 1500);
    };

    // ===== FETCH DATA FROM API =====
    useEffect(() => {
        const fetchData = async () => {
            try {
                console.log('Fetching home data...');

                const [homeData, testimonialsRes, pressRes, partnersRes, coursesRes] = await Promise.all([
                    api.get('/home-data'),
                    api.get('/testimonials'),
                    api.get('/press-releases'),
                    api.get('/partners'),
                    api.get('/courses')
                ]);

                console.log('API Response:', homeData.data);

                // ===== PROCESS SLIDERS FROM API =====
                const apiSliders = homeData.data.sliders || [];
                console.log('Sliders from API:', apiSliders);

                if (apiSliders.length > 0) {
                    const formattedSliders = apiSliders.map((slider) => {
                        let imageUrl = slider.image_path;
                        console.log('Processing slider:', slider.title, 'Image path:', imageUrl);

                        if (imageUrl) {
                            if (imageUrl.startsWith('/storage/')) {
                                imageUrl = `${config.API_BASE_URL}${imageUrl}`;
                            } else if (imageUrl.startsWith('/images/')) {
                                imageUrl = `${config.API_BASE_URL}${imageUrl}`;
                            } else if (!imageUrl.startsWith('http')) {
                                imageUrl = `${config.API_BASE_URL}${imageUrl}`;
                            }
                        }

                        return {
                            id: slider.id,
                            src: imageUrl || '/images/slider/default.jpg',
                            title: slider.title || 'Slide',
                            subtitle: slider.subtitle || '',
                            button_text: slider.button_text || 'Apply Now',
                            button_link: slider.button_link || '/courses',
                        };
                    });

                    console.log('Formatted sliders:', formattedSliders);
                    setDynamicSliders(formattedSliders);
                    setCurrentSlide(0);
                } else {
                    console.log('No sliders from API, using fallback');
                    setDynamicSliders(sliderImages);
                }

                // ✅ Process courses from API
                const apiCourses = coursesRes.data || [];
                if (apiCourses.length > 0) {
                    const formattedCourses = apiCourses.map((course) => ({
                        id: course.id,
                        name: course.title || course.name,
                        category: course.category,
                        description: course.description || '',
                        duration: course.duration,
                        fee: course.fee || '',
                        level: course.level || 'Beginner',
                        is_active: course.is_active,
                        is_featured: course.is_featured,
                    }));
                    setCourses(formattedCourses);
                } else {
                    setCourses(courseData);
                }

                // Process partners from API
                const apiPartners = partnersRes.data || [];
                if (apiPartners.length > 0) {
                    const activePartners = apiPartners.filter(p => p.is_active === true || p.is_active === 1);
                    const formattedPartners = activePartners.map((partner) => {
                        let logoUrl = partner.logo_path;
                        if (logoUrl) {
                            if (logoUrl.startsWith('/storage/')) {
                                logoUrl = `${config.API_BASE_URL}${logoUrl}`;
                            } else if (logoUrl.startsWith('/images/')) {
                                logoUrl = logoUrl;
                            } else if (!logoUrl.startsWith('http')) {
                                logoUrl = `${config.API_BASE_URL}${logoUrl}`;
                            }
                        }
                        return {
                            id: partner.id,
                            name: partner.name,
                            logo: logoUrl || null,
                            website: partner.website || '',
                            icon: '🏢',
                            is_active: partner.is_active
                        };
                    });
                    setPartners(formattedPartners);
                } else {
                    setPartners(fallbackPartners);
                }

                // Process testimonials
                const apiTestimonials = testimonialsRes.data.data || testimonialsRes.data || [];
                if (apiTestimonials.length > 0) {
                    setTestimonials(apiTestimonials);
                } else {
                    setTestimonials(fallbackTestimonials);
                }

                // Process press releases
                const apiPress = pressRes.data.data || pressRes.data || [];
                setPressReleases(apiPress);

                console.log('Loading complete - data loaded');

            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    // ===== AUTO-SLIDE FOR HERO =====
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % dynamicSliders.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [dynamicSliders.length]);

    // ===== AUTO-SLIDE FOR OFFICIALS =====
    useEffect(() => {
        const timer = setInterval(() => {
            setOfficialsSlide((prev) => (prev + 1) % Math.ceil(officials.length / 3));
        }, 4000);
        return () => clearInterval(timer);
    }, []);

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const goToOfficialSlide = (index) => {
        setOfficialsSlide(index);
    };

    // Get current officials to display
    const getVisibleOfficials = () => {
        const start = officialsSlide * 3;
        return officials.slice(start, start + 3);
    };

    // Helper function to get icon for course category
    const getCourseIcon = (category) => {
        const icons = {
            'Designing (Mechanical)': '📐',
            'Mechanical Manufacturing': '⚙️',
            'Civil': '🏗️',
            'Welding & Fabrication': '🔧',
            'Electrical & Electronics': '⚡',
            'Language': '🌐',
            'Health & Safety': '🛡️',
            'EV': '🚗',
            'AI & Technology': '🤖',
            'Foundry & Metal Treatment': '🔥',
            'Pattern, Carpenter & CNC Wood Router': '🪚',
            'Renewable Energy (Solar)': '☀️'
        };
        return icons[category] || '📘';
    };

    return (
        <div className="home-page">
           
            {/* Hero Slider Section */}
            <section className="hero-slider">
                <div className="slider-container">
                    {dynamicSliders.map((slide, index) => (
                        <div
                            key={slide.id || index}
                            className={`slide ${index === currentSlide ? 'active' : ''}`}
                            style={{ backgroundImage: `url(${slide.src})` }}
                        >
                            <div className="slide-overlay"></div>
                            <div className="slide-content">
                                <div className="slide-badge">PITAC</div>
                                <h1 className="slide-title">{slide.title}</h1>
                                <p className="slide-subtitle">{slide.subtitle}</p>
                                <div className="slide-buttons">
                                    <button
                                        className="btn-primary"
                                        onClick={() => window.location.href = slide.button_link || '/courses'}
                                    >
                                        {slide.button_text || 'Apply Now'}
                                    </button>
                                    <button className="btn-secondary">View Programs</button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Slider Navigation Dots */}
                    <div className="slider-dots">
                        {dynamicSliders.map((_, index) => (
                            <button
                                key={index}
                                className={`dot ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                            />
                        ))}
                    </div>

                    {/* Slider Arrows */}
                    <button
                        className="slider-arrow prev"
                        onClick={() => goToSlide((currentSlide - 1 + dynamicSliders.length) % dynamicSliders.length)}
                    >
                        ‹
                    </button>
                    <button
                        className="slider-arrow next"
                        onClick={() => goToSlide((currentSlide + 1) % dynamicSliders.length)}
                    >
                        ›
                    </button>
                </div>

                {/* Stats Bar */}
                <div className="stats-bar">
                    <div className="container">
                        <div className="stats-grid">
                            <div className="stat-item">
                                <span className="stat-number">32,000+</span>
                                <span className="stat-label">Trained Graduates</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <span className="stat-number">68%</span>
                                <span className="stat-label">Employability Rate</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <span className="stat-number">58+</span>
                                <span className="stat-label">Technical Programs</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <span className="stat-number">90+</span>
                                <span className="stat-label">Industry Partners</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TVET Highlights Section */}
            <section className="tvet-highlight">
                <div className="container">
                    <div className="tvet-grid">
                        {tvetFeatures.map((feature, index) => (
                            <div key={index} className="tvet-item">
                                <div className="tvet-icon">{feature.icon}</div>
                                <h4>{feature.title}</h4>
                                <p>{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who We Are Section */}
            <section className="who-we-are">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">About PITAC</span>
                        <h2>Who We Are</h2>
                        <p className="section-subtitle">
                            The Government of Pakistan established Pakistan Industrial Technical Assistance Centre (PITAC) in 1962 with the merger of Industrial Research and Development Centre (IRDC) and Industrial Productivity Centre (IPC) under the administrative control of Ministry of Industries, Government of Pakistan.
                        </p>
                    </div>
                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-number">01</div>
                            <div className="feature-icon">
                                <img
                                    src="/images/target-arrow.jpeg"
                                    alt="Mission Target"
                                    style={{ width: '90px', height: 'auto' }}
                                />
                            </div>
                            <h3>Mission Statement</h3>
                            <p>
                                To upgrade, advise, and extend technical & managerial skill development
                                for sustainable industrial growth and economic value.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-number">02</div>
                            <div className="feature-icon">
                                <img
                                    src="/images/eye-vision.png"
                                    alt="Vision Eye"
                                    style={{ width: '90px', height: 'auto' }}
                                />
                            </div>
                            <h3>Vision Statement</h3>
                            <p>
                                To attain excellence as the premier organization in technical human resource
                                development and industrial spares manufacturing.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-number">03</div>
                            <div className="feature-icon">
                                <img
                                    src="/images/team-target.png"
                                    alt="Core Values Team"
                                    style={{ width: '90px', height: 'auto' }}
                                />
                            </div>
                            <h3>Core Values</h3>
                            <p>
                                To establish PITAC as Pakistan's premier technical training institution with world-class facilities, industry-relevant diplomas, corporate training, customized assistance, and a commitment to accessible education and sustainable growth.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Officials Slider Section */}
            <section className="officials-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">Leadership</span>
                        <h2>Our Leadership Team</h2>
                        <p className="section-subtitle">Guiding Pakistan's technical education & vocational training</p>
                    </div>
                    <div className="officials-slider">
                        <div className="officials-grid">
                            {getVisibleOfficials().map((official) => (
                                <div key={official.id} className="official-card">
                                    <div className="official-image-wrapper">
                                        <img
                                            src={official.image}
                                            alt={official.name}
                                            className="official-image"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UyZTJlMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjQwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmaWxsPSIjOTk5Ij5QVEM8L3RleHQ+PC9zdmc+';
                                            }}
                                        />
                                        <div className="official-overlay">
                                            <span className="official-role-badge">{official.position}</span>
                                        </div>
                                    </div>
                                    <h3 className="official-name">{official.name}</h3>
                                    <p className="official-position">{official.position}</p>
                                    <p className="official-bio">{official.bio}</p>
                                </div>
                            ))}
                        </div>

                        {/* Officials Dots */}
                        <div className="officials-dots">
                            {[...Array(Math.ceil(officials.length / 3))].map((_, index) => (
                                <button
                                    key={index}
                                    className={`dot ${index === officialsSlide ? 'active' : ''}`}
                                    onClick={() => goToOfficialSlide(index)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== REGIONAL CENTRES SECTION ===== */}
            <section className="regional-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">Our Network</span>
                        <h2>Regional Centres</h2>
                        <p className="section-subtitle">PITAC's presence across Pakistan — connecting industry, skills & opportunity</p>
                    </div>
                    <div className="regional-grid">
                        {regionalCentres.map((centre) => (
                            <a key={centre.id} href="#" className="regional-card" aria-label={`Regional Centre ${centre.name}`}>
                                <span className="regional-badge">📍 {centre.region}</span>
                                <span className="regional-icon">{centre.icon}</span>
                                <span className="regional-name">{centre.name}</span>
                                <span className="regional-sub">Regional Centre</span>
                                <span className="regional-arrow">→</span>
                            </a>
                        ))}
                    </div>
                    <div className="regional-footer">
                        <span>📌 5 regional centres · serving communities nationwide</span>
                    </div>
                </div>
            </section>

            {/* ===== COURSES SLIDER SECTION ===== */}
            <section className="courses-slider-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">Our Programs</span>
                        <h2>Industry Ready <span className="highlight-text">Training Programs</span></h2>
                        <p className="section-subtitle">
                            Explore our comprehensive range of technical and managerial training programs
                        </p>
                    </div>

                    {/* Category Filter */}
                    <div className="course-category-filter">
                        <div className="filter-wrapper">
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    className={`filter-chip ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category)}
                                >
                                    {category === 'All Categories' ? '🏷️ All' : category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Course Slider - Auto Scroll */}
                    <div
                        className="course-slider-wrapper"
                        onMouseEnter={handleCourseMouseEnter}
                        onMouseLeave={handleCourseMouseLeave}
                    >
                        <div className="course-slider-viewport" ref={courseSliderRef}>
                            <div className="course-slider-track" ref={courseTrackRef}>
                                {getVisibleCourses().map((course) => (
                                    <div key={course.id} className="course-slider-card">
                                        <div className="course-card-inner">
                                            <div className="course-card-header">
                                                <span className={`level-badge ${course.level.toLowerCase()}`}>
                                                    {course.level}
                                                </span>
                                            </div>

                                            <h4 className="course-card-title">{course.name}</h4>

                                            <span className="category-tag" style={{
                                                background: getCategoryColor(course.category),
                                                color: '#fff'
                                            }}>
                                                {course.category}
                                            </span>

                                            <div className="course-card-details">
                                                <span className="detail-item">
                                                    <span className="detail-icon">⏱️</span>
                                                    <span>{course.duration}</span>
                                                </span>
                                                <span className="detail-item">
                                                    <span className="detail-icon">💰</span>
                                                    <span className="fee-amount">Rs. {course.fee}</span>
                                                </span>
                                            </div>

                                            <button className="course-card-btn">
                                                Enroll Now
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="view-all-courses">
                        <button className="view-all-btn" onClick={() => window.location.href = '/courses'}>
                            View All Programs
                            <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </button>
                    </div>
                </div>
            </section>

            {/* News & Updates Section */}
            <section className="news-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">News & Updates</span>
                        <h2>Latest <span className="highlight-text">News</span></h2>
                        <p className="section-subtitle">
                            Stay informed about PITAC's latest achievements, events, and announcements
                        </p>
                    </div>

                    {pressReleases.length > 0 ? (
                        <div className="news-wrapper">
                            {/* Featured News - First Item */}
                            <div 
                                className="news-featured" 
                                onClick={() => window.location.href = `/press-releases/${pressReleases[0].id}`}
                            >
                                <div className="news-featured-label">Featured</div>
                                <div className="news-featured-content">
                                    <span className="news-featured-category">
                                        {pressReleases[0].category || 'Press Release'}
                                    </span>
                                    <h3 className="news-featured-title">{pressReleases[0].title}</h3>
                                    <p className="news-featured-text">
                                        {pressReleases[0].summary || pressReleases[0].description?.substring(0, 180) || 'Click to read full story'}
                                    </p>
                                    <div className="news-featured-footer">
                                        <span className="news-featured-date">
                                            {formatDate(pressReleases[0].published_date)}
                                        </span>
                                        <button 
                                            className="news-featured-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.location.href = `/press-releases/${pressReleases[0].id}`;
                                            }}
                                        >
                                            Read More →
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* News List - Remaining Items */}
                            <div className="news-list">
                                {pressReleases.slice(1, 6).map((release, index) => (
                                    <div 
                                        key={release.id} 
                                        className="news-item"
                                        onClick={() => window.location.href = `/press-releases/${release.id}`}
                                    >
                                        <div className="news-item-icon">
                                            <span>{index === 0 ? '📌' : '📄'}</span>
                                        </div>
                                        <div className="news-item-body">
                                            <div className="news-item-header">
                                                <span className="news-item-tag">
                                                    {release.category || 'Update'}
                                                </span>
                                                <span className="news-item-date">
                                                    {formatDate(release.published_date)}
                                                </span>
                                            </div>
                                            <h4 className="news-item-title">{release.title}</h4>
                                            <p className="news-item-excerpt">
                                                {release.summary || release.description?.substring(0, 80) || ''}
                                            </p>
                                        </div>
                                        <div className="news-item-arrow">→</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="news-empty">
                            <div className="news-empty-icon">📰</div>
                            <h3>No Updates Available</h3>
                            <p>Check back later for the latest news and announcements.</p>
                        </div>
                    )}

                    <div className="news-footer">
                        <button 
                            className="news-view-all" 
                            onClick={() => window.location.href = '/press-releases'}
                        >
                            View All News →
                        </button>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">Testimonials</span>
                        <h2>What Our Students Say</h2>
                    </div>
                    <div className="testimonials-grid">
                        {testimonials.slice(0, 3).map(testimonial => (
                            <div key={testimonial.id} className="testimonial-card">
                                <div className="testimonial-rating">
                                    {'★'.repeat(testimonial.rating || 5)}
                                </div>
                                <p className="testimonial-content">"{testimonial.content}"</p>
                                <div className="testimonial-author">
                                    <div className="author-avatar">
                                        {testimonial.avatar ? (
                                            <img src={testimonial.avatar} alt={testimonial.name} />
                                        ) : (
                                            <div className="avatar-placeholder">
                                                {testimonial.name.charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <h4>{testimonial.name}</h4>
                                        <p className="author-role">{testimonial.role}</p>
                                    </div>
                                </div>
                                <div className="testimonial-verified">
                                    <span>✓ Verified Graduate</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <div className="cta-badge">
                            <span>Admissions Open 2026</span>
                        </div>
                        <h2>Ready to Kickstart Your Career?</h2>
                        <p className="cta-subtitle">Join Pakistan's premier technical training institution</p>
                        <button className="cta-btn">
                            <span>Apply Now</span>
                            <svg viewBox="0 0 24 24" width="24" height="24">
                                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" />
                            </svg>
                        </button>
                        <div className="cta-features">
                            <span>✓ Industry-Recognized Certifications</span>
                            <span>✓ Expert Faculty</span>
                            <span>✓ Job Placement Support</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Partners Section */}
            <section className="partners-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">Our Partners</span>
                        <h2>Industry & Academic Partners</h2>
                    </div>

                    {partners.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7a8f' }}>
                            <p>No partners added yet.</p>
                        </div>
                    ) : (
                        <div className="partners-wrapper">
                            <button
                                className="partner-nav-btn partner-nav-prev"
                                onClick={scrollPartnersPrev}
                                aria-label="Previous partners"
                            >
                                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>

                            <div
                                className="partners-slider"
                                ref={partnersViewportRef}
                                onMouseEnter={handlePartnersMouseEnter}
                                onMouseLeave={handlePartnersMouseLeave}
                            >
                                <div className="partners-track" ref={partnersTrackRef}>
                                    {[...partners, ...partners].map((partner, index) => (
                                        <div key={`${partner.id}-${index}`} className="partner-item">
                                            {partner.logo ? (
                                                <img
                                                    src={partner.logo}
                                                    alt={`${partner.name} logo`}
                                                    className="partner-logo"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        const fallbackIcon = e.target.parentElement?.querySelector('.partner-fallback-icon');
                                                        if (fallbackIcon) fallbackIcon.style.display = 'block';
                                                    }}
                                                />
                                            ) : (
                                                <span className="partner-fallback-icon">🏢</span>
                                            )}
                                            <span className="partner-name">{partner.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <button
                                className="partner-nav-btn partner-nav-next"
                                onClick={scrollPartnersNext}
                                aria-label="Next partners"
                            >
                                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Home;